import { cn } from '@/lib/utils'
import type { MinerMessage } from '@/types/interview'
import { useEffect, useRef } from 'react'

interface ChatThreadProps {
  messages: MinerMessage[]
  /** Shown as a trailing "typing" row while the interviewer's reply is in flight. */
  pending: boolean
}

export function ChatThread({ messages, pending }: ChatThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on every new message / pending flip
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, pending])

  return (
    <div
      className="space-y-3"
      role="log"
      aria-live="polite"
      aria-label="Conversation with your story coach"
    >
      {messages.map((m, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: append-only transcript, index is identity
          key={i}
          className={cn('flex', m.role === 'student' ? 'justify-end' : 'justify-start')}
        >
          <p
            className={cn(
              'max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
              m.role === 'student'
                ? 'bg-primary text-primary-foreground rounded-br-md'
                : 'bg-muted text-foreground rounded-bl-md'
            )}
          >
            {m.text}
          </p>
        </div>
      ))}
      {pending && (
        <div className="flex justify-start">
          <p className="rounded-2xl rounded-bl-md bg-muted px-4 py-2.5 text-sm text-muted-foreground">
            <span className="animate-pulse">Thinking…</span>
          </p>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  )
}
